import { Alert, Stack } from "@mui/material";
import { useHandleError } from "Hooks/useHandleError";
import { ConfirmCredentialsResponseFail } from "Services/api/profile/interfaces";
import { createToken, verifyToken } from "Services/api/profile/profile";
import FormikForm from "Shared/FormikForm/FormikForm";
import FormikSubmitButton from "Shared/FormikSubmitButton/FormikSubmitButton";
import Password from "Shared/Password/Password";
import { SecretQuestions } from "Shared/SecretQuestionsForm/SecretQuestionsForm";
import { SecretQuestionData } from "Shared/SecretQuestionsForm/interfaces";
import { passwordValidation, requiredValidation } from "Utils/validations";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as Yup from "yup";
import { TokenInputs } from "./interfaces";

export default function ProfileToken({
  secretQuestions,
  onSubmit,
}: {
  secretQuestions: SecretQuestionData[];
  onSubmit: () => void | Promise<void>;
}) {
  const initValues = Object.assign(
    { password: "" },
    ...secretQuestions.map((record, index) => ({
      [`secret-question-${index}`]: record.question,
      [`secret-answer-${index}`]: record.answer || "",
    }))
  ) as TokenInputs;
  const { enqueueSnackbar } = useSnackbar();
  const handleError = useHandleError();
  const [errorKey, setErrorKey] = useState<ConfirmCredentialsResponseFail["errorKey"] | "">("");

  return (
    <Formik
      initialValues={initValues}
      onSubmit={async ({ password, ...questions }, { setSubmitting }) => {
        try {
          const secretQuestions = [...new Array(Object.keys(questions).length / 2).keys()].map((index) => ({
            question: questions[`secret-question-${index}`],
            answer: questions[`secret-answer-${index}`],
          })) as SecretQuestionData[];

          const { status, data } = await createToken({ password, secretQuestions });

          if (status === "success") {
            sessionStorage.setItem("profile-token", data);
            await onSubmit();
          } else {
            setErrorKey(data.errorKey);
            enqueueSnackbar(data.message, { variant: "error" });
          }
          setSubmitting(false);
        } catch (error) {
          handleError(error);
        }
      }}
      validationSchema={Yup.object({
        password: passwordValidation,
        ...Object.assign(
          {},
          ...[...new Array(secretQuestions.length).keys()].map((index) => ({
            [`secret-question-${index}`]: requiredValidation,
            [`secret-answer-${index}`]: requiredValidation,
          }))
        ),
      })}
    >
      <FormikForm width="100%">
        <Stack spacing={2}>
          <Alert severity="info" color="success" variant="outlined">
            Debe de confirmar sus datos de seguridad para poder continuar.
          </Alert>
          <Password
            name="password"
            id="password"
            fullWidth
            label="Confirma tu contraseña actual:"
            variant="outlined"
            required
            InputProps={{
              autoComplete: "current-password",
            }}
          />
          <SecretQuestions data={secretQuestions} questionAsLabel />

          {errorKey === "invalidCredentials" && (
            <Alert severity="error" variant="outlined">
              La contraseña digitada es incorrecta. Intente nuevamente o comuniquese con un oficial de servicio de la
              cooperativa.
            </Alert>
          )}

          {errorKey === "invalidSecretQuestions" && (
            <Alert severity="error" variant="outlined">
              Las respuestas proporcionadas son incorrectas. Intente nuevamente o comuniquese con un oficial de servicio
              de la cooperativa para cambiar sus preguntas de seguridad.
            </Alert>
          )}

          {errorKey === "secretQuestionsError" && (
            <Alert severity="error" variant="outlined">
              Ha ocurrido un error al intentar validar las preguntas de seguridad. Intente nuevamente o comuniquese con
              un oficial de servicio de la cooperativa.
            </Alert>
          )}

          <FormikSubmitButton fullWidth variant="contained">
            Continuar
          </FormikSubmitButton>
        </Stack>
      </FormikForm>
    </Formik>
  );
}

export async function checkToken(
  onValid: (token: string) => void | Promise<void>,
  onInvalid: () => void | Promise<void>
) {
  const token = sessionStorage.getItem("profile-token");
  if (!token) return onInvalid();

  const { data: validToken } = await verifyToken(token);
  if (!validToken) return onInvalid();

  return onValid(token);
}
